import { useState, useEffect } from 'react';
import EntityLink from '../components/EntityLink';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import { useToast, useConfirm } from '../context/FeedbackContext';
import { useAuth } from '../context/AuthContext';

const ENGAGEMENT_LABELS = {
  contract_placement: 'Contract Placement',
  recurring_referral: 'Recurring Referral',
  fte_placement: 'FTE Placement',
};

const TABS = ['Profile', 'Work Orders & Rates', 'Invoices', 'AP Ledger', 'AR Ledger', 'Advances', 'Documents'];

function fmt(n) {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ContractorProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('Profile');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  function load() {
    setError(false);
    api.get(`/contractors/${id}`)
      .then(r => { setData(r.data); setForm(r.data); })
      .catch(() => setError(true));
  }
  useEffect(() => { load(); }, [id]);

  function sf(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      await api.put(`/contractors/${id}`, form);
      load();
      setEditing(false);
    } catch (e) {
      setSaveError(e.response?.data?.error || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  }

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="page-title">{data.first_name} {data.last_name}</h1>
          <Badge variant={data.computed_status === 'active' ? 'success' : 'neutral'}>
            {data.computed_status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
          <Badge variant="info">{(data.contractor_subtype || 'corp_to_corp').replace('_', '-').toUpperCase()}</Badge>
        </div>
      </div>

      <div className="panel-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Profile' && (
        <ProfileTab
          data={data} editing={editing} form={form} sf={sf}
          onEdit={() => { setEditing(true); setForm(data); setSaveError(''); }}
          onCancel={() => { setEditing(false); setSaveError(''); }}
          onSave={handleSave} saving={saving} saveError={saveError}
        />
      )}

      {tab === 'Work Orders & Rates' && <WorkOrdersRatesTab data={data} onChanged={load} navigate={navigate} />}

      {tab === 'Invoices' && <InvoicesTab data={data} />}

      {tab === 'AP Ledger' && <ContractorLedgerTab contractorId={data.id} side="ap" />}

      {tab === 'AR Ledger' && <ContractorLedgerTab contractorId={data.id} side="ar" />}

      {tab === 'Advances' && <AdvancesTab contractorId={data.id} workOrders={data.work_orders || []} />}

      {tab === 'Documents' && <DocumentsTab contractorId={data.id} entityName={`${data.first_name} ${data.last_name}`} />}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function ProfileTab({ data, editing, form, sf, onEdit, onCancel, onSave, saving, saveError }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Contractor Profile</h3>
        <button className="btn btn-secondary" onClick={editing ? onCancel : onEdit}>{editing ? 'Cancel' : 'Edit'}</button>
      </div>

      {saveError && (
        <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
          {saveError}
        </div>
      )}

      {editing ? (
        <>
          <div className="form-grid">
            <div className="form-field"><label>First Name</label><input value={form.first_name || ''} onChange={e => sf('first_name', e.target.value)} /></div>
            <div className="form-field"><label>Middle Name</label><input value={form.middle_name || ''} onChange={e => sf('middle_name', e.target.value)} /></div>
            <div className="form-field"><label>Last Name</label><input value={form.last_name || ''} onChange={e => sf('last_name', e.target.value)} /></div>
            <div className="form-field"><label>Email</label><input type="email" value={form.email || ''} onChange={e => sf('email', e.target.value)} /></div>
            <div className="form-field"><label>Phone</label><input value={form.phone || ''} onChange={e => sf('phone', e.target.value)} /></div>
            <div className="form-field">
              <label>Contractor Subtype</label>
              <select value={form.contractor_subtype || 'corp_to_corp'} onChange={e => sf('contractor_subtype', e.target.value)}>
                <option value="corp_to_corp">Corp-to-Corp</option>
                <option value="1099">1099</option>
              </select>
            </div>
            <div className="form-field"><label>Work Authorization</label><input value={form.work_authorization || ''} onChange={e => sf('work_authorization', e.target.value)} /></div>
            <div className="form-field"><label>Work Auth Expiry</label><input type="date" value={form.work_auth_expiry || ''} onChange={e => sf('work_auth_expiry', e.target.value)} /></div>
            <div className="form-field"><label>LinkedIn</label><input value={form.linkedin || ''} onChange={e => sf('linkedin', e.target.value)} /></div>
          </div>
          <h4 style={{ marginTop: 20, marginBottom: 10 }}>Bank Info</h4>
          <div className="form-grid">
            <div className="form-field"><label>Bank Name</label><input value={form.bank_name || ''} onChange={e => sf('bank_name', e.target.value)} /></div>
            <div className="form-field"><label>Routing Number</label><input value={form.routing_number || ''} onChange={e => sf('routing_number', e.target.value)} /></div>
            <div className="form-field"><label>Account Number</label><input value={form.account_number || ''} onChange={e => sf('account_number', e.target.value)} /></div>
            <div className="form-field">
              <label>Account Type</label>
              <select value={form.account_type || 'Checking'} onChange={e => sf('account_type', e.target.value)}>
                <option value="Checking">Checking</option>
                <option value="Savings">Savings</option>
              </select>
            </div>
          </div>

          <div className="form-field span-2" style={{ marginTop: 14 }}><label>Address</label><textarea rows={2} value={form.address || ''} onChange={e => sf('address', e.target.value)} /></div>
          <div className="form-field span-2" style={{ marginTop: 14 }}><label>Notes</label><textarea rows={3} value={form.notes || ''} onChange={e => sf('notes', e.target.value)} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Field label="Email" value={data.email} />
          <Field label="Phone" value={data.phone} />
          <Field label="Work Authorization" value={data.work_authorization} />
          <Field label="Work Auth Expiry" value={data.work_auth_expiry} />
          <Field label="LinkedIn" value={data.linkedin} />
          <Field label="Subtype" value={(data.contractor_subtype || 'corp_to_corp').replace('_', '-')} />
          <div style={{ gridColumn: '1 / -1' }}><Field label="Address" value={data.address} /></div>
          {data.notes && <div style={{ gridColumn: '1 / -1' }}><Field label="Notes" value={data.notes} /></div>}
        </div>
      )}

      {!editing && (data.bank_name || data.account_number || data.routing_number) && (
        <>
          <h4 style={{ marginTop: 20, marginBottom: 10 }}>Bank Info</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <Field label="Bank Name" value={data.bank_name} />
            <Field label="Routing Number" value={data.routing_number} />
            <Field label="Account Number" value={data.account_number} />
            <Field label="Account Type" value={data.account_type} />
          </div>
        </>
      )}
    </div>
  );
}

function WorkOrdersRatesTab({ data, onChanged, navigate }) {
  const toast = useToast();
  const [editingRate, setEditingRate] = useState(null);
  const [rateInput, setRateInput] = useState('');
  const [editingBuyRate, setEditingBuyRate] = useState(null);
  const [buyRateInput, setBuyRateInput] = useState('');

  const sortedPOs = [...(data.purchase_orders || [])].sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (a.status !== 'open' && b.status === 'open') return 1;
    return new Date(b.period_start || 0) - new Date(a.period_start || 0);
  });
  const openPO = sortedPOs.find(p => p.status === 'open');
  const currentSellRate = openPO?.sell_rate;
  const currentBuyRate = openPO?.buy_rate;
  const currentMargin = currentSellRate && currentBuyRate ? (currentSellRate - currentBuyRate) : null;

  async function saveSellRate(poId) {
    if (!rateInput || isNaN(parseFloat(rateInput))) return;
    try {
      await api.put(`/purchase-orders/${poId}`, { sell_rate: parseFloat(rateInput) });
      setEditingRate(null); setRateInput(''); onChanged();
    } catch (e) { toast(e.response?.data?.error || 'Could not save sell rate', 'error'); }
  }
  async function saveBuyRate(poId) {
    if (!buyRateInput || isNaN(parseFloat(buyRateInput))) return;
    try {
      await api.put(`/purchase-orders/${poId}`, { buy_rate: parseFloat(buyRateInput) });
      setEditingBuyRate(null); setBuyRateInput(''); onChanged();
    } catch (e) { toast(e.response?.data?.error || 'Could not save buy rate', 'error'); }
  }

  return (
    <>
      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Work Orders</h3>
        {(!data.work_orders || data.work_orders.length === 0) ? (
          <StateBlock type="empty" title="No work orders" />
        ) : data.work_orders.map(wo => (
          <div key={wo.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 600 }}>{wo.client_wo_number}</span>
              <span style={{ marginLeft: 10, fontSize: 13, color: 'var(--muted)' }}>
                {ENGAGEMENT_LABELS[wo.engagement_type] || wo.engagement_type} · {wo.client_name || '—'}{wo.vendor_name ? ` via ${wo.vendor_name}` : ''}
              </span>
            </div>
            <Badge variant={wo.status === 'active' ? 'success' : 'neutral'}>{wo.status}</Badge>
          </div>
        ))}
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Purchase Orders & Rate History</h3>

        {sortedPOs.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Sell Rate</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{currentSellRate ? `$${currentSellRate}/hr` : '—'}</div>
            </div>
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Buy Rate</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{currentBuyRate ? `$${currentBuyRate}/hr` : '—'}</div>
            </div>
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Margin</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: currentMargin != null ? 'var(--success)' : undefined }}>
                {currentMargin != null ? `$${currentMargin.toFixed(2)}/hr` : '—'}
              </div>
            </div>
            <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Total POs</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{sortedPOs.length}</div>
            </div>
          </div>
        )}

        <table className="data-table">
          <thead>
            <tr>
              <th>PO Number</th><th>Client</th><th style={{ textAlign: 'right' }}>Sell Rate</th>
              <th style={{ textAlign: 'right' }}>Buy Rate</th><th style={{ textAlign: 'right' }}>Margin</th>
              <th>Period</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedPOs.length === 0 && <tr><td colSpan={7}><StateBlock type="empty" title="No purchase orders" /></td></tr>}
            {sortedPOs.map(po => (
              <tr key={po.id}>
                <td style={{ fontWeight: po.status === 'open' ? 600 : 400 }}>{po.po_number}</td>
                <td>{po.client_name}</td>
                <td style={{ textAlign: 'right' }}>
                  {editingRate === po.id ? (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <input type="number" step="0.01" value={rateInput} onChange={e => setRateInput(e.target.value)}
                        style={{ width: 70 }} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') saveSellRate(po.id); if (e.key === 'Escape') { setEditingRate(null); setRateInput(''); } }} />
                      <button className="btn btn-ghost" onClick={() => saveSellRate(po.id)}>✓</button>
                      <button className="btn btn-ghost" onClick={() => { setEditingRate(null); setRateInput(''); }}>✕</button>
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      {po.sell_rate ? `$${po.sell_rate}/hr` : '—'}
                      {po.status === 'open' && (
                        <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setEditingRate(po.id); setRateInput(po.sell_rate || ''); }}>✎</button>
                      )}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {editingBuyRate === po.id ? (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <input type="number" step="0.01" value={buyRateInput} onChange={e => setBuyRateInput(e.target.value)}
                        style={{ width: 70 }} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') saveBuyRate(po.id); if (e.key === 'Escape') { setEditingBuyRate(null); setBuyRateInput(''); } }} />
                      <button className="btn btn-ghost" onClick={() => saveBuyRate(po.id)}>✓</button>
                      <button className="btn btn-ghost" onClick={() => { setEditingBuyRate(null); setBuyRateInput(''); }}>✕</button>
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      {po.buy_rate ? `$${po.buy_rate}/hr` : '—'}
                      {po.status === 'open' && (
                        <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setEditingBuyRate(po.id); setBuyRateInput(po.buy_rate || ''); }}>✎</button>
                      )}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {po.sell_rate && po.buy_rate ? `$${(po.sell_rate - po.buy_rate).toFixed(2)}/hr` : '—'}
                </td>
                <td>{po.period_start} – {po.period_end}</td>
                <td><Badge variant={po.status === 'open' ? 'success' : 'neutral'}>{po.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function InvoicesTab({ data }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>AR Billed</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_ar_billed)}</div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>AR Verified Received</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_ar_verified_received)}</div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>AP Billed</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_ap_billed)}</div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>AP Verified Paid</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_ap_verified_paid)}</div>
        </div>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>AR Invoices ({data.ar_invoices.length})</h3>
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Client</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Verified Paid</th><th>Status</th></tr></thead>
          <tbody>
            {data.ar_invoices.length === 0 && <tr><td colSpan={6}><StateBlock type="empty" title="No AR invoices" /></td></tr>}
            {data.ar_invoices.map(inv => (
              <tr key={inv.id}>
                <td><EntityLink type="ar_invoice" id={inv.id}>{inv.invoice_number}</EntityLink></td><td><EntityLink type="client" id={inv.client_id}>{inv.client_name}</EntityLink></td><td>{inv.invoice_date}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.amount)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.verified_paid)}</td>
                <td><Badge>{inv.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>AP Invoices ({data.ap_invoices.length})</h3>
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Vendor</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Verified Paid</th><th>Status</th></tr></thead>
          <tbody>
            {data.ap_invoices.length === 0 && <tr><td colSpan={6}><StateBlock type="empty" title="No AP invoices" /></td></tr>}
            {data.ap_invoices.map(inv => (
              <tr key={inv.id}>
                <td>{inv.invoice_number}</td><td>{inv.vendor_name || '—'}</td><td>{inv.invoice_date}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.amount_to_pay)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.verified_paid)}</td>
                <td><Badge>{inv.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ContractorLedgerTab({ contractorId, side }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    setLedger(null);
    api.get(`/contractors/${contractorId}/ledger/${side}`).then(r => setLedger(r.data));
  }, [contractorId, side]);

  if (!ledger) return <StateBlock type="loading" />;
  if (ledger.rows.length === 0) return <StateBlock type="empty" title="No ledger activity yet" message={`No ${side.toUpperCase()} invoices for this contractor yet.`} />;

  const otherPartyKey = side === 'ap' ? 'vendor_name' : 'client_name';

  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ledger Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(ledger.summary.ledger_balance)}</div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Verified Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(ledger.summary.verified_balance)}</div>
        </div>
      </div>
      <table className="data-table">
        <thead><tr><th>Invoice #</th><th>{side === 'ap' ? 'Vendor' : 'Client'}</th><th style={{ textAlign: 'right' }}>Amount</th><th>Paid</th><th>Status</th></tr></thead>
        <tbody>
          {ledger.rows.map((r, i) => (
            <tr key={i}>
              <td><EntityLink type="ar_invoice" id={r.id}>{r.invoice_number}</EntityLink></td>
              <td>{r[otherPartyKey] || '—'}</td>
              <td style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
              <td>{r.payment_date || '—'}</td>
              <td>{r.verification_status ? <Badge variant={r.verification_status === 'verified' ? 'success' : 'warning'}>{r.verification_status}</Badge> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Advances (2026-10-27): money advanced to the contractor on its own bank
// trace, and the recoveries taken from AP payments (advance deductions).
const EMPTY_ADVANCE = { advance_date: '', amount: '', wo_id: '', trace_id: '', payment_method: '', note: '' };

function AdvancesTab({ contractorId, workOrders }) {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  const [ledger, setLedger] = useState(null);
  const confirm = useConfirm();
  const [form, setForm] = useState(null);   // null = closed; { id?, ...fields }
  const [voiding, setVoiding] = useState(null);   // { id, date, amount, reason }
  const [saving, setSaving] = useState(false);

  function load() {
    api.get(`/contractors/${contractorId}/advances`).then(r => setLedger(r.data)).catch(() => toast('Could not load advances', 'error'));
  }
  useEffect(load, [contractorId]);

  async function save() {
    setSaving(true);
    try {
      const body = { ...form, wo_id: form.wo_id || null, amount: Number(form.amount) };
      if (form.id) await api.put(`/contractors/${contractorId}/advances/${form.id}`, body);
      else await api.post(`/contractors/${contractorId}/advances`, body);
      toast(form.id ? 'Advance updated' : 'Advance recorded', 'success');
      setForm(null);
      load();
    } catch (e) { toast(e.response?.data?.error || 'Could not save advance', 'error'); }
    setSaving(false);
  }

  async function voidAdvance() {
    if (!voiding.reason.trim()) { toast('Give a reason for voiding the advance', 'error'); return; }
    if (!(await confirm({ title: 'Void advance?', message: `The ${fmt(voiding.amount)} advance of ${voiding.date} will no longer count in the balance. The record stays in the audit log.`, confirmLabel: 'Void', danger: true }))) return;
    try {
      await api.post(`/contractors/${contractorId}/advances/${voiding.id}/void`, { reason: voiding.reason });
      toast('Advance voided', 'success');
      setVoiding(null);
      load();
    } catch (err) { toast(err.response?.data?.error || 'Could not void advance', 'error'); }
  }

  if (!ledger) return <StateBlock type="loading" />;
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const tile = (label, value, color) => (
    <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmt(value)}</div>
    </div>
  );

  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Advances</h3>
        {isAdmin && !form && <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setForm({ ...EMPTY_ADVANCE })}>+ Record advance</button>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        {tile('Advanced', ledger.total_advanced)}
        {tile('Recovered (deductions)', ledger.total_recovered)}
        {tile('Balance owed by contractor', ledger.balance, ledger.balance < 0 ? 'var(--danger)' : undefined)}
      </div>
      {ledger.balance < 0 && (
        <div className="warning-strip">Recoveries exceed the advances recorded here – an earlier advance has not been recorded yet.</div>
      )}

      {form && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <div className="form-field"><label>Date</label><input type="date" value={form.advance_date || ''} onChange={e => sf('advance_date', e.target.value)} /></div>
          <div className="form-field"><label>Amount</label><input type="number" step="0.01" value={form.amount} onChange={e => sf('amount', e.target.value)} /></div>
          <div className="form-field"><label>Work Order</label>
            <select value={form.wo_id || ''} onChange={e => sf('wo_id', e.target.value)}>
              <option value="">—</option>
              {workOrders.map(wo => <option key={wo.id} value={wo.id}>{wo.client_wo_number}</option>)}
            </select>
          </div>
          <div className="form-field"><label>Bank trace ID</label><input value={form.trace_id || ''} onChange={e => sf('trace_id', e.target.value)} /></div>
          <div className="form-field"><label>Payment method</label><input value={form.payment_method || ''} placeholder="PNC bill pay" onChange={e => sf('payment_method', e.target.value)} /></div>
          <div className="form-field"><label>Note</label><input value={form.note || ''} onChange={e => sf('note', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      {voiding && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <div className="form-field" style={{ flex: 1, margin: 0 }}>
            <label>Reason for voiding the {fmt(voiding.amount)} advance of {voiding.date}</label>
            <input value={voiding.reason} onChange={ev => setVoiding(v => ({ ...v, reason: ev.target.value }))} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={voidAdvance}>Void</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setVoiding(null)}>Cancel</button>
        </div>
      )}

      {ledger.entries.length === 0 ? (
        <StateBlock type="empty" title="No advances" message="No advances or advance recoveries for this contractor." />
      ) : (
        <table className="data-table">
          <thead><tr><th>Date</th><th>Type</th><th>Invoice / WO</th><th>Trace ID</th><th style={{ textAlign: 'right' }}>Advanced</th><th style={{ textAlign: 'right' }}>Recovered</th><th style={{ textAlign: 'right' }}>Balance</th><th>Note</th>{isAdmin && <th />}</tr></thead>
          <tbody>
            {ledger.entries.map((e, i) => (
              <tr key={i}>
                <td>{e.date}</td>
                <td>{e.kind === 'advance' ? <Badge variant="warning">Advance paid</Badge> : <Badge variant="success">Recovery</Badge>}</td>
                <td>{e.kind === 'recovery' ? <EntityLink type="ap_invoice" id={e.ap_invoice_id}>{e.invoice_number}</EntityLink> : (e.client_wo_number || '—')}</td>
                <td style={{ fontSize: 12, color: 'var(--muted)' }}>{e.trace_id || ''}</td>
                <td style={{ textAlign: 'right' }}>{e.kind === 'advance' ? fmt(e.amount) : ''}</td>
                <td style={{ textAlign: 'right' }}>{e.kind === 'recovery' ? fmt(e.amount) : ''}</td>
                <td style={{ textAlign: 'right' }}>{fmt(e.balance)}</td>
                <td style={{ fontSize: 12, color: 'var(--muted)' }}>{e.note || ''}</td>
                {isAdmin && (
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {e.kind === 'advance' && <>
                      <button className="btn btn-secondary btn-sm" onClick={() => setForm({ id: e.advance_id, advance_date: e.date, amount: e.amount, wo_id: e.wo_id || '', trace_id: e.trace_id || '', payment_method: e.payment_method || '', note: e.note || '' })}>Edit</button>{' '}
                      <button className="btn btn-secondary btn-sm" onClick={() => setVoiding({ id: e.advance_id, date: e.date, amount: e.amount, reason: '' })}>Void</button>
                    </>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
        Advances are not AP invoices and not AP cost. They are recovered through the "advance deduction" on this contractor's AP payments.
      </div>
    </div>
  );
}

function DocumentsTab({ contractorId, entityName }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <DocumentWallet entityType="contractor" entityId={contractorId} entityName={entityName} />
    
      <AuditLog tableName="contractors" recordId={contractorId} /></div>
  );
}
